import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    Zap,
    TrendingUp,
    BarChart3,
    ShieldCheck,
    Clock,
    ArrowDown,
    Percent,
    ChevronRight,
    CheckCircle2,
    ArrowRight,
} from 'lucide-react';

const programs = [
    {
        name: 'Fast FX',
        icon: Zap,
        badge: 'Single Phase',
        description: 'Accelerated path to funding with a single evaluation phase.',
        accounts: '$10K - $100K',
        profitTarget: '15%',
        phases: 'Single Phase',
        leverage: '1:20',
        profitSplit: '80%',
        startingPrice: '$99',
        features: ['Single evaluation phase', '15% profit target', '1:20 leverage', '80% profit split'],
    },
    {
        name: 'Regular',
        icon: TrendingUp,
        badge: 'Most Popular',
        description: 'Our standard two-phase evaluation with balanced trading conditions.',
        accounts: '$10K - $100K',
        profitTarget: '10% + 8%',
        phases: 'Two Phases',
        leverage: '1:50',
        profitSplit: '80%',
        startingPrice: '$79',
        features: ['Two-phase evaluation', '10% + 8% profit targets', '1:50 leverage', '80% profit split'],
        popular: true,
    },
    {
        name: 'Swing',
        icon: BarChart3,
        badge: 'Weekend Trading',
        description: 'Designed for swing traders with weekend and news trading enabled.',
        accounts: '$10K - $100K',
        profitTarget: '10% + 8%',
        phases: 'Two Phases',
        leverage: '1:30',
        profitSplit: '80%',
        startingPrice: '$89',
        features: ['Weekend & news trading', '10% + 8% profit targets', '1:30 leverage', '80% profit split'],
    },
];

const tradingRules = [
    { icon: Clock, label: 'No Time Limit', description: 'Trade at your own pace with no deadline' },
    { icon: CheckCircle2, label: '3-Day Minimum', description: 'Minimum of 3 trading days required' },
    { icon: ArrowDown, label: '5% Max Daily Loss', description: 'Maximum daily drawdown limit' },
    { icon: Percent, label: '10% Max Overall Loss', description: 'Maximum overall drawdown limit' },
];

const processSteps = [
    { step: 1, title: 'Choose Program', description: 'Select the funding program that matches your trading style and goals.' },
    { step: 2, title: 'Pass Evaluation', description: 'Demonstrate your trading skills by meeting the profit targets within the rules.' },
    { step: 3, title: 'Get Funded', description: 'Receive your funded trading account and start trading with real capital.' },
    { step: 4, title: 'Earn Profits', description: 'Keep up to 80% of the profits you generate on your funded account.' },
];

export default function Overview() {
    return (
        <AppLayout title={`Funding Programs - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="bg-background pt-32 pb-16 lg:pt-40 lg:pb-20">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Funding Programs</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Funding <span className="text-gradient">Programs</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                            Choose Your Path to Professional Trading
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Program Comparison */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Choose Your"
                        highlight="Program"
                        description="Compare our funding programs and find the perfect fit for your trading strategy."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {programs.map((program) => {
                            const Icon = program.icon;
                            return (
                                <motion.div key={program.name} variants={staggerItem}>
                                    <Card
                                        className={`relative flex flex-col h-full rounded-xl border bg-card/80 backdrop-blur-sm ${
                                            program.popular
                                                ? 'border-primary/40 bg-primary/[0.03]'
                                                : 'border-border'
                                        }`}
                                    >
                                        {program.popular && (
                                            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
                                        )}
                                        {program.popular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                <Badge>Most Popular</Badge>
                                            </div>
                                        )}
                                        <CardHeader className="text-center">
                                            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                <Icon className="h-7 w-7 text-primary" />
                                            </div>
                                            <CardTitle className="text-2xl">{program.name}</CardTitle>
                                            <CardDescription>{program.description}</CardDescription>
                                        </CardHeader>
                                        <CardContent className="flex-1 space-y-4">
                                            <div className="text-center">
                                                <span className="text-sm text-muted-foreground">Starting from</span>
                                                <p className="text-3xl font-bold text-foreground">{program.startingPrice}</p>
                                            </div>
                                            <div className="space-y-3">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Accounts</span>
                                                    <span className="font-medium text-foreground">{program.accounts}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Profit Target</span>
                                                    <span className="font-medium text-foreground">{program.profitTarget}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Phases</span>
                                                    <span className="font-medium text-foreground">{program.phases}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Leverage</span>
                                                    <span className="font-medium text-foreground">{program.leverage}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Profit Split</span>
                                                    <span className="font-medium text-foreground">{program.profitSplit}</span>
                                                </div>
                                            </div>
                                            <ul className="space-y-2 pt-2">
                                                {program.features.map((feature) => (
                                                    <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                                                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardContent>
                                        <CardFooter>
                                            <a
                                                href={siteConfig.portal.register}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-full"
                                            >
                                                <Button className="w-full" size="lg" variant={program.popular ? 'default' : 'outline'}>
                                                    Buy Challenge
                                                    <ArrowRight className="ml-2 h-4 w-4" />
                                                </Button>
                                            </a>
                                        </CardFooter>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Trading Rules Summary */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Trading"
                        highlight="Rules"
                        description="Simple, transparent rules designed to help you succeed."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        {tradingRules.map((rule) => {
                            const Icon = rule.icon;
                            return (
                                <motion.div key={rule.label} variants={staggerItem}>
                                    <Card className="text-center p-6 rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                            <Icon className="h-6 w-6 text-primary" />
                                        </div>
                                        <h3 className="font-semibold text-foreground mb-1">{rule.label}</h3>
                                        <p className="text-sm text-muted-foreground">{rule.description}</p>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
                <FadeIn>
                    <div className="text-center">
                        <Link to="/funding/rules">
                            <Button variant="outline" size="lg">
                                View All Trading Rules
                                <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </FadeIn>
            </Section>

            {/* Process Steps */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="How It"
                        highlight="Works"
                        description="Four simple steps to becoming a funded trader."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {processSteps.map((item) => (
                            <motion.div key={item.step} variants={staggerItem} className="text-center">
                                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-xl">
                                    {item.step}
                                </div>
                                <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-background">
                <FadeIn>
                    <div className="text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Ready to Start <span className="text-gradient">Trading</span>?
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                            Choose your funding program and begin your journey to becoming a professional funded trader today.
                        </p>
                        <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                            <Button size="xl">
                                Get Started Now
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </a>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
